import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

/**
 * Responsive header-actions wrapper.
 *
 * On desktop (≥sm) the children render inline as a normal toolbar
 * row. On mobile (<sm) a single hamburger button replaces them, and
 * tapping it opens a **bottom sheet** that slides up from the bottom
 * edge with the SAME children stacked vertically. One source of
 * truth for the actions; the wrapper picks the layout per breakpoint.
 *
 * Why bottom sheet over right-edge drawer:
 *   • Thumb-zone ergonomics — bottom is reachable on tall phones
 *     where the top-right corner needs a hand shuffle.
 *   • Mobile-native — Twitter / Facebook / Instagram action menus
 *     all use bottom sheets; users instantly recognise the pattern.
 *   • Drag handle gives a visual affordance for "drag down to
 *     dismiss" (the body click-handler also dismisses).
 *
 * Rendered via `createPortal(document.body)` so the sheet escapes
 * any ancestor with `backdrop-filter` / `transform` / `filter`,
 * which would otherwise create a containing block and pin the
 * `position: fixed` overlay inside the ancestor's bounding box
 * (e.g. trapped behind the GameHeader).
 *
 * Usage:
 *   <MobileMenu>
 *     <Button>...</Button>
 *     <Button>...</Button>
 *   </MobileMenu>
 *
 * Children should be self-contained buttons. The sheet auto-closes
 * on any button tap inside it, on backdrop click, on Escape, and on
 * a swipe-down gesture exceeding 80px.
 */
export default function MobileMenu({
  children,
  desktopChildren,
  triggerLabel,
  className = "",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false); // drives the slide-out animation
  const [dragOffset, setDragOffset] = useState(0); // live drag offset while user pulls the sheet down
  const sheetRef = useRef(null);
  const triggerRef = useRef(null);
  const dragStartRef = useRef(null);

  // Animated close — flip `closing` to true, wait for the transform
  // transition (200ms), then unmount. Keeps the slide-out smooth.
  // Memoised so the Escape-listener effect's dep array stays stable.
  const close = useCallback(() => {
    if (!open || closing) return;
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setDragOffset(0);
    }, 200);
  }, [open, closing]);

  // Click-outside (anywhere outside the sheet content) + Escape to
  // close. The trigger is excluded so toggling on the same button
  // doesn't immediately reopen.
  useEffect(() => {
    if (!open || closing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closing, close]);

  // Lock the body scroll while the sheet is open so the page beneath
  // doesn't scroll under the user's finger on iOS.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Touch handlers for the drag-to-dismiss gesture on the sheet
  // header. Pulling the sheet down >80px commits to a close; smaller
  // drags snap back to position.
  const onDragStart = (e) => {
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    dragStartRef.current = y;
    setDragOffset(0);
  };
  const onDragMove = (e) => {
    if (dragStartRef.current == null) return;
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    const delta = Math.max(0, y - dragStartRef.current); // only allow downward drag
    setDragOffset(delta);
  };
  const onDragEnd = () => {
    if (dragStartRef.current == null) return;
    if (dragOffset > 80) {
      close();
    } else {
      setDragOffset(0);
    }
    dragStartRef.current = null;
  };

  const label = triggerLabel || t("common.menu", "Menu");
  // Translation applied inline so the close-animation can flip it
  // back to off-screen-bottom.
  const sheetTransform = closing
    ? "translateY(100%)"
    : `translateY(${dragOffset}px)`;

  return (
    <>
      {/* Desktop — inline toolbar. `desktopChildren` overrides if
          you want a tighter / icon-only desktop variant. */}
      <div className={`hidden sm:flex items-center gap-2 ${className}`}>
        {desktopChildren ?? children}
      </div>

      {/* Mobile — hamburger trigger. */}
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        onClick={() => (open ? close() : setOpen(true))}
        className="sm:hidden p-1.5"
        aria-label={label}
        aria-expanded={open}
        data-testid="mobile-menu-trigger"
      >
        <Menu className="w-5 h-5" aria-hidden="true" />
      </Button>

      {/* Bottom sheet portal. The fixed wrapper covers the viewport;
          the sheet itself is anchored to the bottom and animates in
          via `transform`. */}
      {open && typeof document !== "undefined" && createPortal(
        <div
          className="sm:hidden fixed inset-0 z-[100]"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          data-testid="mobile-menu-drawer"
        >
          {/* Backdrop — fades in/out alongside the sheet. */}
          <div
            className={`absolute inset-0 bg-black/55 backdrop-blur-sm transition-opacity duration-200 ${
              closing ? "opacity-0" : "opacity-100"
            }`}
            aria-hidden="true"
            onClick={close}
          />
          {/* The sheet. `pb-safe` covers the iOS home-indicator;
              `max-h-[85vh]` keeps the sheet from covering the entire
              screen so users can still see what's underneath. */}
          <div
            ref={sheetRef}
            className={`absolute left-0 right-0 bottom-0 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] ${
              closing ? "" : "animate-slide-up"
            }`}
            style={{
              transform: sheetTransform,
              transition: dragStartRef.current == null ? "transform 200ms cubic-bezier(0.32,0.72,0,1)" : "none",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            {/* Drag handle — visual cue for "drag down to dismiss",
                also a touch-target for the drag gesture. */}
            <div
              className="pt-2 pb-1 flex justify-center cursor-grab touch-none select-none"
              onTouchStart={onDragStart}
              onTouchMove={onDragMove}
              onTouchEnd={onDragEnd}
              onMouseDown={onDragStart}
              onMouseMove={dragStartRef.current != null ? onDragMove : undefined}
              onMouseUp={onDragEnd}
              onMouseLeave={dragStartRef.current != null ? onDragEnd : undefined}
            >
              <span
                className="block w-10 h-1.5 rounded-full bg-[#1A237E]/20 dark:bg-[#E3F2FD]/20"
                aria-hidden="true"
              />
            </div>

            {/* Header — title + explicit close. The drag handle
                covers ergonomic dismissal; the X is the
                accessibility / non-touch fallback. */}
            <div className="flex items-center justify-between px-5 pt-1 pb-3 border-b border-[#1A237E]/10 dark:border-[#E3F2FD]/10">
              <span
                className="text-base font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                style={{ fontFamily: "Fredoka" }}
              >
                {label}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={close}
                className="p-1.5 -mr-1.5"
                aria-label={t("common.close", "Close")}
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </Button>
            </div>

            {/* Action stack. Auto-closes the sheet on any button tap.
                Larger tap targets (min h-12) for thumb-friendly use.
                Children that want to opt out of auto-close (e.g. a
                multi-step action) should call e.stopPropagation()
                on their own onClick. */}
            <div
              className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1.5 [&_button]:h-12 [&_button]:text-base [&_button]:justify-start [&_button]:rounded-xl"
              onClick={(e) => {
                if (e.target.closest("button")) close();
              }}
            >
              {children}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
